import { ApexOptions } from 'apexcharts';
import { format, subDays } from 'date-fns';
import { Profitability, Subordination } from 'src/types/operations/fidc';

const DEFAULT_THREE_DAYS_RANGE = 24 * 60 * 60 * 1000 * 3;

type ApexLocale = {
  name?: string;
  options?: {
    months?: string[];
    shortMonths?: string[];
    days?: string[];
    shortDays?: string[];
    toolbar?: {
      download?: string;
      selection?: string;
      selectionZoom?: string;
      zoomIn?: string;
      zoomOut?: string;
      pan?: string;
      reset?: string;
      exportToSVG?: string;
      exportToPNG?: string;
      exportToCSV?: string;
    };
  };
};

export type QuotaAccumulator = Record<
  string,
  { name: string; values: Record<string, number> }
>;

export const locale: ApexLocale = {
  name: 'pt-BR',
  options: {
    months: [
      'jan',
      'fev',
      'mar',
      'abr',
      'mai',
      'jun',
      'jul',
      'ago',
      'set',
      'out',
      'nov',
      'dez',
    ],
    shortMonths: [
      'jan',
      'fev',
      'mar',
      'abr',
      'mai',
      'jun',
      'jul',
      'ago',
      'set',
      'out',
      'nov',
      'dez',
    ],
    shortDays: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'],
    days: [
      'domingo',
      'segunda',
      'terça',
      'quarta',
      'quinta',
      'sexta',
      'sábado',
    ],
  },
};

export const colors = [
  '#FF8211',
  '#000000',
  '#FF6B35',
  '#454545',
  '#FFAF7A',
  '#737373',
  '#FFD1A6',
  '#333333',
  '#FFB46B',
  '#999999',
];

export function getPeriodDate(days: number | null) {
  if (!days) return null;
  return format(subDays(new Date(), days), 'yyyy-MM-dd');
}

export function getQuotaKey(item: any): string {
  return `${item.cota}-${(item.serie ?? 0).toString()}`;
}

export function getQuotaLabel(cota: string, serie?: number | null): string {
  const serieValue = serie ?? 0;
  return `${cota} ${serieValue === 0 ? 'Única' : serieValue}`;
}

export function customTooltip({
  series,
  seriesIndex,
  dataPointIndex,
  w,
}: {
  series: any;
  seriesIndex: number;
  dataPointIndex: number;
  w: any;
}) {
  const date = w.globals.seriesX[seriesIndex][dataPointIndex];
  const formattedDate = new Date(date).toLocaleDateString('pt-BR');

  let tooltipContent =
    '<div style="padding: 10px; background: white; border: 1px solid #ccc; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">';
  tooltipContent += `<div style="font-weight: bold; margin-bottom: 8px; color: #333;">${formattedDate}</div>`;

  let hasData = false;
  series.forEach((serie: any, index: number) => {
    const value = serie[dataPointIndex];
    if (value !== null && value !== undefined && value !== 0) {
      hasData = true;
      const seriesName = w.globals.seriesNames[index];
      const formattedValue = value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });
      tooltipContent +=
        '<div style="margin: 4px 0; display: flex; align-items: center;">';
      tooltipContent += `<span style="display: inline-block; width: 12px; height: 12px; background-color: ${w.globals.colors[index]}; border-radius: 50%; margin-right: 8px;"></span>`;
      tooltipContent += `<strong style="margin-right: 8px;">${seriesName}:</strong>`;
      tooltipContent += `<span>${formattedValue}</span>`;
      tooltipContent += '</div>';
    }
  });

  if (!hasData) {
    tooltipContent +=
      '<div style="color: #666;">Nenhum dado disponível para esta data</div>';
  }

  tooltipContent += '</div>';
  return tooltipContent;
}

export function customSubordinationTooltip({
  series,
  seriesIndex,
  dataPointIndex,
  w,
}: {
  series: any;
  seriesIndex: number;
  dataPointIndex: number;
  w: any;
}) {
  const date = w.globals.seriesX[seriesIndex][dataPointIndex];
  const formattedDate = new Date(date).toLocaleDateString('pt-BR');

  let tooltipContent =
    '<div style="padding: 10px; background: white; border: 1px solid #ccc; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">';
  tooltipContent += `<div style="font-weight: bold; margin-bottom: 8px; color: #333;">${formattedDate}</div>`;

  let hasData = false;
  series.forEach((serie: any, index: number) => {
    const value = serie[dataPointIndex];
    if (value !== null && value !== undefined && value !== 0) {
      hasData = true;
      const seriesName = w.globals.seriesNames[index];
      const formattedValue =
        value.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + '%';
      tooltipContent +=
        '<div style="margin: 4px 0; display: flex; align-items: center;">';
      tooltipContent += `<span style="display: inline-block; width: 12px; height: 12px; background-color: ${w.globals.colors[index]}; border-radius: 50%; margin-right: 8px;"></span>`;
      tooltipContent += `<strong style="margin-right: 8px;">${seriesName}:</strong>`;
      tooltipContent += `<span>${formattedValue}</span>`;
      tooltipContent += '</div>';
    }
  });

  if (!hasData) {
    tooltipContent +=
      '<div style="color: #666;">Nenhum dado disponível para esta data</div>';
  }

  tooltipContent += '</div>';
  return tooltipContent;
}

export const noData: Pick<ApexOptions, 'noData'>['noData'] = {
  text: 'Nenhum registro encontrado para o período selecionado',
  align: 'center',
  verticalAlign: 'middle',
  offsetX: 0,
  offsetY: 0,
  style: {
    color: '#666',
    fontSize: '16px',
    fontFamily: 'Inter',
  },
};

export function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export const fakeProfitabilityData = Array(30)
  .fill(0)
  .flatMap((_, dateIndex) =>
    Array(3)
      .fill(0)
      .map((_, quotaIndex) => ({
        id: dateIndex * 3 + quotaIndex,
        nomeFundo: `Fundo ${quotaIndex + 1}`,
        cnpj: `1234567890${quotaIndex}`,
        provedor: quotaIndex,
        cota: Math.random() > 0.5 ? 'Senior' : 'Mezanino',
        serie: quotaIndex,
        data: new Date(2025, 0, dateIndex + 1).toISOString().split('T')[0],
        rentabilidadeDiaria: 0.05 + Math.random() * 0.2,
      })),
  ) as Profitability[];

export const fakeSubordinationData = Array(30)
  .fill(0)
  .flatMap((_, dateIndex) =>
    Array(2)
      .fill(0)
      .map((_, quotaIndex) => ({
        id: dateIndex * 2 + quotaIndex,
        nomeFundo: `Fundo ${quotaIndex + 1}`,
        cnpj: `1234567890${quotaIndex}`,
        provedor: quotaIndex,
        cota: quotaIndex === 0 ? 'Senior' : 'Mezanino',
        serie: quotaIndex,
        data: new Date(2025, 0, dateIndex + 1).toISOString().split('T')[0],
        plCota: 1000000 + Math.random() * 500000,
        plFundo: 5000000 + Math.random() * 1000000,
        plSenior: 3000000 + Math.random() * 500000,
        plMezanino: 1500000 + Math.random() * 300000,
        plSub: 500000 + Math.random() * 100000,
        subordinacaoSenior: 15 + Math.random() * 5,
        subordinacaoMezanino: 25 + Math.random() * 5,
        subordinacaoSub: 35 + Math.random() * 5,
        ismulticlasse: true,
        operationid: quotaIndex,
      })),
  ) as Subordination[];

export function limitZoomToRange(
  rangeLimit: number,
  chartContext: any,
  xaxis: any,
) {
  if (!xaxis) return;

  const range = xaxis.max - xaxis.min;

  if (range < rangeLimit) {
    const center = (xaxis.max + xaxis.min) / 2;
    chartContext.zoomX(center - rangeLimit / 2, center + rangeLimit / 2);
  }
}

export function getLimitZoomMountedHandler(
  rangeLimit: number = DEFAULT_THREE_DAYS_RANGE,
) {
  return (chartContext: any) => {
    const { minX, maxX } = chartContext.w.globals;
    if (typeof minX === 'number' && typeof maxX === 'number') {
      limitZoomToRange(rangeLimit, chartContext, {
        min: minX,
        max: maxX,
      });
    }
  };
}
